import { useCallback, useEffect, useRef, useState } from "react";
import { invoiceManager } from "../../data/managers/InvoiceManager";
import {
  CreateInvoiceBillingReminderParams,
  CreateInvoiceQuoteParams,
  CreateInvoiceSaleParams,
  InvoiceBillingReminderFilters,
  InvoiceBillingReminderModel,
  InvoiceBillingReminderSummaryModel,
  InvoiceCatalogItemModel,
  InvoiceCatalogPayload,
  InvoiceCategoryModel,
  InvoiceCustomerModel,
  InvoiceLyproCompanyModel,
  InvoicePaymentModel,
  InvoiceQuoteModel,
  InvoiceQuotePreviewResult,
  InvoiceSaleModel,
  InvoiceSalesFilters,
  InvoiceSalesSummaryModel,
  InvoiceSellerModel,
  InvoiceUsageQuoteModel,
  UpdateInvoiceBillingReminderParams,
} from "../../data/models/Invoices/InvoiceModels";

const EMPTY_CATALOG: InvoiceCatalogPayload = {
  categories: [],
  items: [],
  customers: [],
  sellers: [],
};

const dateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const yearStartRange = () => {
  const now = new Date();
  return {
    from: dateInputValue(new Date(now.getFullYear(), 0, 1)),
    to: dateInputValue(now),
  };
};

const defaultSalesFilters = (): InvoiceSalesFilters => {
  const range = yearStartRange();
  return {
    status: "all",
    from: range.from,
    to: range.to,
  };
};

const defaultBillingReminderFilters = (): InvoiceBillingReminderFilters => {
  const range = yearStartRange();
  return {
    status: "pending",
    from: range.from,
    to: range.to,
  };
};

const EMPTY_SUMMARY: InvoiceSalesSummaryModel = {
  status: "all",
  invoiceCount: 0,
  voidedInvoiceCount: 0,
  totalSales: 0,
  totalCost: 0,
  commissionTotal: 0,
  paidTotal: 0,
  balanceTotal: 0,
  grossProfit: 0,
  grossMarginPercent: 0,
  pendingInvoiceCount: 0,
  overdueInvoiceCount: 0,
  overdueBalanceTotal: 0,
  averageTicket: 0,
  daily: [],
  sellers: [],
  items: [],
};

const EMPTY_REMINDER_SUMMARY: InvoiceBillingReminderSummaryModel = {
  status: "pending",
  reminderCount: 0,
  pendingCount: 0,
  billedCount: 0,
  cancelledCount: 0,
  pendingTotal: 0,
  billedTotal: 0,
  overduePendingCount: 0,
  overduePendingTotal: 0,
};

const normalizeSalesFilters = (
  filters?: InvoiceSalesFilters
): InvoiceSalesFilters => ({
  status: filters?.status || "all",
  from: filters?.from || undefined,
  to: filters?.to || undefined,
});

const normalizeBillingReminderFilters = (
  filters?: InvoiceBillingReminderFilters
): InvoiceBillingReminderFilters => ({
  status: filters?.status || "pending",
  from: filters?.from || undefined,
  to: filters?.to || undefined,
});

export function useInvoices() {
  const defaultSalesFiltersRef = useRef<InvoiceSalesFilters>(
    defaultSalesFilters()
  );
  const [catalog, setCatalog] =
    useState<InvoiceCatalogPayload>(EMPTY_CATALOG);
  const [lyproCompanies, setLyproCompanies] = useState<
    InvoiceLyproCompanyModel[]
  >([]);
  const [quotes, setQuotes] = useState<InvoiceQuoteModel[]>([]);
  const [sales, setSales] = useState<InvoiceSaleModel[]>([]);
  const [salesSummary, setSalesSummary] =
    useState<InvoiceSalesSummaryModel>(EMPTY_SUMMARY);
  const [billingReminders, setBillingReminders] = useState<
    InvoiceBillingReminderModel[]
  >([]);
  const [billingReminderSummary, setBillingReminderSummary] =
    useState<InvoiceBillingReminderSummaryModel>(EMPTY_REMINDER_SUMMARY);
  const [activeSalesFilters, setActiveSalesFilters] =
    useState<InvoiceSalesFilters>(defaultSalesFiltersRef.current);
  const activeSalesFiltersRef = useRef<InvoiceSalesFilters>(
    defaultSalesFiltersRef.current
  );
  const defaultBillingReminderFiltersRef =
    useRef<InvoiceBillingReminderFilters>(defaultBillingReminderFilters());
  const [activeBillingReminderFilters, setActiveBillingReminderFilters] =
    useState<InvoiceBillingReminderFilters>(
      defaultBillingReminderFiltersRef.current
    );
  const activeBillingReminderFiltersRef = useRef<InvoiceBillingReminderFilters>(
    defaultBillingReminderFiltersRef.current
  );
  const [loading, setLoading] = useState(true);
  const [salesLoading, setSalesLoading] = useState(false);
  const [billingRemindersLoading, setBillingRemindersLoading] = useState(false);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );

  const notify = useCallback(
    (message: string, severity: "success" | "error" = "success") => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    },
    []
  );

  const loadSalesData = useCallback(async (filters?: InvoiceSalesFilters) => {
    const normalizedFilters = normalizeSalesFilters(filters);
    const [nextSales, nextSummary] = await Promise.all([
      invoiceManager.getSales(normalizedFilters),
      invoiceManager.getSalesSummary(normalizedFilters),
    ]);
    setSales(nextSales);
    setSalesSummary(nextSummary || EMPTY_SUMMARY);
    setActiveSalesFilters(normalizedFilters);
    activeSalesFiltersRef.current = normalizedFilters;
  }, []);

  const loadBillingReminderData = useCallback(
    async (filters?: InvoiceBillingReminderFilters) => {
      const normalizedFilters = normalizeBillingReminderFilters(filters);
      const [nextReminders, nextSummary] = await Promise.all([
        invoiceManager.getBillingReminders(normalizedFilters),
        invoiceManager.getBillingReminderSummary(normalizedFilters),
      ]);
      setBillingReminders(nextReminders);
      setBillingReminderSummary(nextSummary || EMPTY_REMINDER_SUMMARY);
      setActiveBillingReminderFilters(normalizedFilters);
      activeBillingReminderFiltersRef.current = normalizedFilters;
    },
    []
  );

  const loadQuotesData = useCallback(async () => {
    const nextQuotes = await invoiceManager.getQuotes();
    setQuotes(nextQuotes);
  }, []);

  const refreshSales = useCallback(
    async (filters?: InvoiceSalesFilters) => {
      setSalesLoading(true);
      try {
        await loadSalesData(filters || activeSalesFiltersRef.current);
      } catch (error) {
        notify("No se pudieron actualizar las facturas", "error");
      } finally {
        setSalesLoading(false);
      }
    },
    [loadSalesData, notify]
  );

  const refreshBillingReminders = useCallback(
    async (filters?: InvoiceBillingReminderFilters) => {
      setBillingRemindersLoading(true);
      try {
        await loadBillingReminderData(
          filters || activeBillingReminderFiltersRef.current
        );
      } catch (error) {
        notify("No se pudieron actualizar los recordatorios", "error");
      } finally {
        setBillingRemindersLoading(false);
      }
    },
    [loadBillingReminderData, notify]
  );

  const refreshQuotes = useCallback(async () => {
    setQuotesLoading(true);
    try {
      await loadQuotesData();
    } catch (error) {
      notify("No se pudo actualizar el historial de cotizaciónes", "error");
    } finally {
      setQuotesLoading(false);
    }
  }, [loadQuotesData, notify]);

  const refresh = useCallback(async () => {
    try {
      const [
        nextCatalog,
        nextLyproCompanies,
        nextQuotes,
        nextSales,
        nextSummary,
        nextReminders,
        nextReminderSummary,
      ] = await Promise.all([
        invoiceManager.getCatalog(),
        invoiceManager.getLyproCompanies(),
        invoiceManager.getQuotes(),
        invoiceManager.getSales(activeSalesFiltersRef.current),
        invoiceManager.getSalesSummary(activeSalesFiltersRef.current),
        invoiceManager.getBillingReminders(
          activeBillingReminderFiltersRef.current
        ),
        invoiceManager.getBillingReminderSummary(
          activeBillingReminderFiltersRef.current
        ),
      ]);
      setCatalog(nextCatalog || EMPTY_CATALOG);
      setLyproCompanies(nextLyproCompanies);
      setQuotes(nextQuotes);
      setSales(nextSales);
      setSalesSummary(nextSummary || EMPTY_SUMMARY);
      setBillingReminders(nextReminders);
      setBillingReminderSummary(nextReminderSummary || EMPTY_REMINDER_SUMMARY);
    } catch (error) {
      notify("No se pudo cargar la facturación", "error");
    }
  }, [notify]);

  useEffect(() => {
    let mounted = true;
    const initialize = async () => {
      setLoading(true);
      try {
        await refresh();
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };
    void initialize();
    return () => {
      mounted = false;
    };
  }, [refresh]);

  async function runAction<T>(
    action: () => Promise<T>,
    successMessage: string,
    refreshAfter = true
  ): Promise<T | undefined> {
    setSaving(true);
    try {
      const result = await action();
      if (result === undefined || result === false) {
        throw new Error("Invoice action failed");
      }
      if (refreshAfter) {
        await refresh();
      }
      notify(successMessage);
      return result;
    } catch (error) {
      notify("No se pudo completar la operación", "error");
      return undefined;
    } finally {
      setSaving(false);
    }
  }

  const getUsageQuote = useCallback(
    async (params: {
      customerId: string;
      itemId: string;
      month?: string;
    }): Promise<InvoiceUsageQuoteModel | undefined> => {
      setSaving(true);
      try {
        const result = await invoiceManager.getUsageQuote(params);
        if (!result) {
          throw new Error("Usage quote failed");
        }
        return result;
      } catch (error) {
        notify("No se pudo calcular el consumo LYPRO", "error");
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [notify]
  );

  const previewQuote = useCallback(
    async (
      payload: CreateInvoiceQuoteParams
    ): Promise<InvoiceQuotePreviewResult | undefined> => {
      setSaving(true);
      try {
        const result = await invoiceManager.previewQuote(payload);
        if (!result?.html) {
          throw new Error("Quote preview failed");
        }
        return result;
      } catch (error) {
        notify("No se pudo previsualizar la cotización", "error");
        return undefined;
      } finally {
        setSaving(false);
      }
    },
    [notify]
  );

  return {
    catalog,
    lyproCompanies,
    quotes,
    sales,
    salesSummary,
    billingReminders,
    billingReminderSummary,
    activeSalesFilters,
    activeBillingReminderFilters,
    loading,
    salesLoading,
    billingRemindersLoading,
    quotesLoading,
    saving,
    snackbarOpen,
    snackbarMessage,
    snackbarSeverity,
    setSnackbarOpen,
    refresh,
    refreshSales,
    refreshBillingReminders,
    refreshQuotes,
    createCategory: (payload: Partial<InvoiceCategoryModel>) =>
      runAction(
        () => invoiceManager.createCategory(payload),
        "Categoría guardada"
      ),
    updateCategory: (id: string, payload: Partial<InvoiceCategoryModel>) =>
      runAction(
        () => invoiceManager.updateCategory(id, payload),
        "Categoría actualizada"
      ),
    deleteCategory: (id: string) =>
      runAction(() => invoiceManager.deleteCategory(id), "Categoría eliminada"),
    createItem: (payload: Partial<InvoiceCatalogItemModel>) =>
      runAction(() => invoiceManager.createItem(payload), "Ítem guardado"),
    updateItem: (id: string, payload: Partial<InvoiceCatalogItemModel>) =>
      runAction(() => invoiceManager.updateItem(id, payload), "Ítem actualizado"),
    deleteItem: (id: string) =>
      runAction(() => invoiceManager.deleteItem(id), "Ítem eliminado"),
    createCustomer: (payload: Partial<InvoiceCustomerModel>) =>
      runAction(() => invoiceManager.createCustomer(payload), "Cliente guardado"),
    updateCustomer: (id: string, payload: Partial<InvoiceCustomerModel>) =>
      runAction(
        () => invoiceManager.updateCustomer(id, payload),
        "Cliente actualizado"
      ),
    deleteCustomer: (id: string) =>
      runAction(() => invoiceManager.deleteCustomer(id), "Cliente eliminado"),
    createSeller: (payload: Partial<InvoiceSellerModel>) =>
      runAction(() => invoiceManager.createSeller(payload), "Vendedor guardado"),
    updateSeller: (id: string, payload: Partial<InvoiceSellerModel>) =>
      runAction(
        () => invoiceManager.updateSeller(id, payload),
        "Vendedor actualizado"
      ),
    deleteSeller: (id: string) =>
      runAction(() => invoiceManager.deleteSeller(id), "Vendedor eliminado"),
    createBillingReminder: (payload: CreateInvoiceBillingReminderParams) =>
      runAction(
        () => invoiceManager.createBillingReminder(payload),
        "Recordatorio guardado"
      ),
    updateBillingReminder: (
      id: string,
      payload: UpdateInvoiceBillingReminderParams
    ) =>
      runAction(
        () => invoiceManager.updateBillingReminder(id, payload),
        "Recordatorio actualizado"
      ),
    renewBillingReminder: (id: string, payload?: CreateInvoiceSaleParams) =>
      runAction(
        () => invoiceManager.renewBillingReminder(id, payload),
        "Factura generada desde recordatorio"
      ),
    cancelBillingReminder: (id: string, reason?: string) =>
      runAction(
        () => invoiceManager.cancelBillingReminder(id, reason),
        "Recordatorio cancelado"
      ),
    deleteBillingReminder: (id: string) =>
      runAction(
        () => invoiceManager.deleteBillingReminder(id),
        "Recordatorio eliminado"
      ),
    createQuote: (payload: CreateInvoiceQuoteParams) =>
      runAction(
        () => invoiceManager.createQuote(payload),
        "Cotizacion generada"
      ),
    createSale: (payload: CreateInvoiceSaleParams) =>
      runAction(() => invoiceManager.createSale(payload), "Factura generada"),
    regenerateSalePdfs: (saleId: string) =>
      runAction(
        () => invoiceManager.regenerateSalePdfs(saleId),
        "PDFs generados"
      ),
    addPayment: (saleId: string, payload: Partial<InvoicePaymentModel>) =>
      runAction(
        () => invoiceManager.addPayment(saleId, payload),
        "Abono registrado"
      ),
    deletePayment: (saleId: string, paymentId: string) =>
      runAction(
        () => invoiceManager.deletePayment(saleId, paymentId),
        "Abono eliminado"
      ),
    voidSale: (saleId: string) =>
      runAction(() => invoiceManager.voidSale(saleId), "Factura anulada"),
    getUsageQuote,
    previewQuote,
  };
}
